import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import type {
  ContractWithDetails,
  ContractStatus,
  Payment,
  QuoteWithDetails,
} from '@everplan/shared';
import { CONTRACT_STATUS_LABELS } from '@everplan/shared';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatCents } from '@/lib/format';
import { CurrencyInput } from '@/components/ui/currency-input';
import CollapsibleCard from '@/components/CollapsibleCard';
import DocumentSplit from '@/components/DocumentSplit';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Date-only fields (dueDate, signedAt) are stored as UTC-midnight timestamps.
  // Format in UTC so the displayed date matches the stored date regardless of
  // the viewer's timezone (otherwise PST viewers see the day before).
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

// ─── Status badges ────────────────────────────────────────────────────────────

const CONTRACT_STATUS_STYLES: Record<ContractStatus, string> = {
  requested: 'bg-[var(--color-surface-container-high)] text-muted-foreground',
  received: 'bg-[var(--color-tertiary-container)] text-foreground',
  signed: 'bg-primary text-primary-foreground',
};

function ContractStatusPill({ status }: { status: ContractStatus }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CONTRACT_STATUS_STYLES[status]}`}>
      {CONTRACT_STATUS_LABELS[status]}
    </span>
  );
}

function PaymentStatusBadge({ payment }: { payment: Payment }) {
  const paid = !!payment.paidAt;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${paid ? 'text-primary' : 'text-muted-foreground'}`}>
      {paid ? <CheckCircle2 className="size-3.5" /> : null}
      {paid ? 'Paid' : 'Unpaid'}
    </span>
  );
}

// ─── Select helpers ───────────────────────────────────────────────────────────

const selectClass =
  'h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 text-sm outline-none focus-visible:border-ring';

const selectSmClass =
  'h-8 rounded-md border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-2 text-sm outline-none focus-visible:border-ring';

// ─── Active contract card (one per active contract) ───────────────────────────

interface ActiveContractCardProps {
  contract: ContractWithDetails;
  itemId: string;
  headingLabel: string;
}

function ActiveContractCard({ contract, itemId, headingLabel }: ActiveContractCardProps) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['item', itemId] });

  const [expanded, setExpanded] = useState(true);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [paymentDesc, setPaymentDesc] = useState('');
  const [paymentAmount, setPaymentAmount] = useState<number | undefined>(undefined);
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);

  const updateContractStatus = useMutation({
    mutationFn: async (status: ContractStatus) => {
      const body: Record<string, unknown> = { status };
      if (status === 'signed') body.signedAt = new Date().toISOString();
      const res = await fetch(`/api/contracts/${contract.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to update contract');
    },
    onSuccess: invalidate,
  });

  const deactivateContract = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/contracts/${contract.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to deactivate contract');
    },
    onSuccess: invalidate,
  });

  const addPayment = useMutation({
    mutationFn: async () => {
      setPaymentError('');
      const res = await fetch('/api/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contractId: contract.id,
          description: paymentDesc.trim(),
          amount: paymentAmount ?? 0,
          dueDate: paymentDueDate,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Failed to add payment');
      }
    },
    onSuccess: () => {
      invalidate();
      setPaymentDesc(''); setPaymentAmount(undefined); setPaymentDueDate('');
    },
    onError: (e: Error) => setPaymentError(e.message),
  });

  const updatePaymentPaidAt = useMutation({
    mutationFn: async ({ id, paidAt }: { id: string; paidAt: string | null }) => {
      const res = await fetch(`/api/payments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ paidAt: paidAt ? new Date(paidAt).toISOString() : null }),
      });
      if (!res.ok) throw new Error('Failed to update payment');
    },
    onSuccess: invalidate,
  });

  const deletePayment = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/payments/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to delete payment');
    },
    onSuccess: () => { setDeletePaymentId(null); invalidate(); },
  });

  const paymentsTotalCents = contract.payments.reduce((s, m) => s + m.amount, 0);
  const paidTotalCents = contract.payments.reduce((s, m) => s + (m.paidAt ? m.amount : 0), 0);

  return (
    <CollapsibleCard
      header={
        <div className="flex flex-wrap items-center gap-2">
          <p className="label-caps text-xs text-muted-foreground">{headingLabel}</p>
          <ContractStatusPill status={contract.status} />
        </div>
      }
      trailing={
        <span className="label-caps text-xs tabular-nums text-muted-foreground shrink-0">
          {formatCents(contract.totalAmount)}
        </span>
      }
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
    >
      <DocumentSplit
        documentUrl={contract.sourceDocumentUrl}
        documentMimeType={contract.sourceDocumentMimeType}
      >
        <div className="space-y-6 px-5 pt-4 pb-5">
          {/* Controls row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <select
              value={contract.status}
              onChange={(e) => updateContractStatus.mutate(e.target.value as ContractStatus)}
              disabled={updateContractStatus.isPending}
              className={selectSmClass}
            >
              <option value="requested">Requested</option>
              <option value="received">Received</option>
              <option value="signed">Signed</option>
            </select>
            <Button size="sm" variant="destructive" onClick={() => setConfirmDeactivate(true)} disabled={deactivateContract.isPending}>
              Deactivate
            </Button>
            <DeleteConfirmDialog
              open={confirmDeactivate}
              onOpenChange={setConfirmDeactivate}
              onConfirm={() => deactivateContract.mutate()}
              isPending={deactivateContract.isPending}
              title="Deactivate contract?"
              description="This contract will be moved to history and a new one can be created."
            />
          </div>

          {/* Contract meta */}
          <div className="flex flex-wrap gap-6">
            <div className="space-y-0.5">
              <p className="label-caps text-xs text-muted-foreground">Contract Total</p>
              <p className="text-sm font-semibold tabular-nums">{formatCents(contract.totalAmount)}</p>
            </div>
            {paymentsTotalCents > 0 && (
              <div className="space-y-0.5">
                <p className="label-caps text-xs text-muted-foreground">Scheduled</p>
                <p className="text-sm tabular-nums">{formatCents(paymentsTotalCents)}</p>
              </div>
            )}
            {paidTotalCents > 0 && (
              <div className="space-y-0.5">
                <p className="label-caps text-xs text-muted-foreground">Paid</p>
                <p className="text-sm tabular-nums text-primary font-medium">{formatCents(paidTotalCents)}</p>
              </div>
            )}
            {contract.signedAt && (
              <div className="space-y-0.5">
                <p className="label-caps text-xs text-muted-foreground">Signed</p>
                <p className="text-sm font-medium">{formatDate(contract.signedAt)}</p>
              </div>
            )}
          </div>
          {contract.notes && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contract.notes}</p>
          )}

          {/* Payment Schedule */}
          <div className="space-y-2">
            <p className="label-caps text-xs text-muted-foreground">Payment Schedule</p>

            {contract.payments.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No payments yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Description</th>
                      <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Amount</th>
                      <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Due</th>
                      <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Date Paid</th>
                      <th className="pb-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {contract.payments.map((m) => (
                      <tr key={m.id} className="border-b last:border-0">
                        <td className="py-2 pr-3">{m.description}</td>
                        <td className="py-2 pr-3 tabular-nums font-medium">{formatCents(m.amount)}</td>
                        <td className="py-2 pr-3 text-muted-foreground text-xs">{formatDate(m.dueDate)}</td>
                        <td className="py-2 pr-3"><PaymentStatusBadge payment={m} /></td>
                        <td className="py-2 pr-3">
                          <input
                            type="date"
                            defaultValue={m.paidAt ? m.paidAt.slice(0, 10) : ''}
                            className="h-7 rounded-md border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-2 text-xs outline-none focus-visible:border-ring"
                            onChange={(e) =>
                              updatePaymentPaidAt.mutate({ id: m.id, paidAt: e.target.value || null })
                            }
                          />
                        </td>
                        <td className="py-2">
                          <Button variant="ghost" size="icon-xs" onClick={() => setDeletePaymentId(m.id)} aria-label="Delete payment">
                            <Trash2 className="size-3 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add payment form */}
            <div className="rounded-2xl border border-dashed border-[var(--color-outline-variant)] p-3 space-y-2">
              <p className="label-caps text-xs text-muted-foreground">Add Payment</p>
              <div className="flex flex-wrap gap-2">
                <Input placeholder="Description" value={paymentDesc} onChange={(e) => setPaymentDesc(e.target.value)} className="h-8 flex-1 min-w-[140px] text-sm" />
                <CurrencyInput valueCents={paymentAmount} onChangeCents={setPaymentAmount} className="h-8 w-32 text-sm" placeholder="0.00" />
                <Input type="date" value={paymentDueDate} onChange={(e) => setPaymentDueDate(e.target.value)} className="h-8 w-40 text-sm" />
              </div>
              {paymentError && <p className="text-xs text-destructive">{paymentError}</p>}
              <div className="flex justify-end">
                <Button size="sm" variant="secondary" onClick={() => addPayment.mutate()} disabled={addPayment.isPending || !paymentDesc.trim() || paymentAmount === undefined || paymentAmount <= 0 || !paymentDueDate}>
                  {addPayment.isPending ? 'Adding…' : 'Add Payment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DocumentSplit>

      <DeleteConfirmDialog
        open={deletePaymentId !== null}
        onOpenChange={(open) => { if (!open) setDeletePaymentId(null); }}
        onConfirm={() => { if (deletePaymentId) deletePayment.mutate(deletePaymentId); }}
        isPending={deletePayment.isPending}
        title="Delete payment?"
        description="This payment will be permanently removed."
      />
    </CollapsibleCard>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ContractSectionProps {
  itemId: string;
  contracts: ContractWithDetails[];
  selectedQuote?: QuoteWithDetails | null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ContractSection({ itemId, contracts, selectedQuote }: ContractSectionProps) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['item', itemId] });

  const activeContracts = contracts.filter((c) => c.isActive);
  const historicalContracts = contracts.filter((c) => !c.isActive);

  const [showNewContractForm, setShowNewContractForm] = useState(false);
  const [ncAmount, setNcAmount] = useState<number | undefined>(undefined);
  const [ncNotes, setNcNotes] = useState('');
  const [ncQuoteId, setNcQuoteId] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const createContract = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          itemId,
          quoteId: ncQuoteId || undefined,
          totalAmount: ncAmount ?? 0,
          notes: ncNotes.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to create contract');
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setNcAmount(undefined); setNcNotes(''); setNcQuoteId('');
      setShowNewContractForm(false);
    },
  });

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <p className="label-caps text-xs text-muted-foreground">Contracts</p>
        <Button size="sm" variant="outline" onClick={() => setShowNewContractForm((v) => !v)}>
          {showNewContractForm ? 'Cancel' : '+ New Contract'}
        </Button>
      </div>

      {/* New contract form */}
      {showNewContractForm && (
        <Card className="rounded-3xl bg-card shadow-ambient">
          <CardHeader className="pb-2">
            <p className="label-caps text-xs text-muted-foreground">New Contract</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <label className="label-caps text-xs text-muted-foreground">Total Amount ($) *</label>
              <CurrencyInput valueCents={ncAmount} onChangeCents={setNcAmount} placeholder="0.00" />
            </div>
            {selectedQuote && (
              <div className="space-y-1.5">
                <label className="label-caps text-xs text-muted-foreground">Link to Quote (optional)</label>
                <select value={ncQuoteId} onChange={(e) => setNcQuoteId(e.target.value)} className={selectClass}>
                  <option value="">— None —</option>
                  <option value={selectedQuote.id}>{selectedQuote.name} (selected quote)</option>
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="label-caps text-xs text-muted-foreground">Notes (optional)</label>
              <Textarea placeholder="Any notes…" value={ncNotes} onChange={(e) => setNcNotes(e.target.value)} rows={2} />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => createContract.mutate()} disabled={createContract.isPending || ncAmount === undefined || ncAmount <= 0}>
                {createContract.isPending ? 'Creating…' : 'Create Contract'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active contracts (one card each) */}
      {activeContracts.length > 0 ? (
        activeContracts.map((c, i) => (
          <ActiveContractCard
            key={c.id}
            contract={c}
            itemId={itemId}
            headingLabel={
              activeContracts.length > 1
                ? `Active Contract ${i + 1} of ${activeContracts.length}`
                : 'Active Contract'
            }
          />
        ))
      ) : (
        !showNewContractForm && (
          <Card className="rounded-3xl bg-card shadow-ambient">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground italic">No active contract. Create one above.</p>
            </CardContent>
          </Card>
        )
      )}

      {/* Historical contracts */}
      {historicalContracts.length > 0 && (
        <div className="space-y-2">
          <button type="button" onClick={() => setShowHistory((v) => !v)} className="flex items-center gap-1.5 label-caps text-xs text-muted-foreground hover:text-foreground transition-colors">
            {showHistory ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            {historicalContracts.length} deactivated contract{historicalContracts.length !== 1 ? 's' : ''}
          </button>
          {showHistory && (
            <div className="space-y-2">
              {historicalContracts.map((c) => (
                <Card key={c.id} className="rounded-3xl bg-card shadow-ambient opacity-60">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <ContractStatusPill status={c.status} />
                      <span className="tabular-nums font-medium">{formatCents(c.totalAmount)}</span>
                      {c.signedAt && <span className="text-muted-foreground text-xs">Signed {formatDate(c.signedAt)}</span>}
                      <span className="text-muted-foreground text-xs ml-auto">Created {formatDate(c.createdAt)}</span>
                    </div>
                    {c.notes && <p className="mt-1.5 text-xs text-muted-foreground whitespace-pre-wrap">{c.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
